import React, { useEffect, useMemo, useRef } from 'react';
import { S20Choices, S20ChoicesProps, S20ChoiceCardProps, MilanGradientColors } from '@milanlaser/mlcomponents';
import { processBackgroundAndGradientColorMap, useImageKey } from '../../utils/builder.utils';
import { PaddingSize, paddingMap } from '../../utils/builderPaddingOptions';
import './S20Choices.css';

/**
 * WEBDEV-4191 — give S20Choices' card images a real `src`.
 *
 * `S20ChoiceCard` renders `<ImageWrapper src={image} />` with no `renderImage`, so it always
 * falls back to mlcomponents' `DefaultImg`. That component keeps the URL in `data-src` and only
 * promotes it to `src` from inside an IntersectionObserver callback which, on this page, never
 * lands the URL: measured on production, all seven card images stay at `naturalHeight: 0` and
 * some end up with `src=""` after the observer runs and unobserves, so it never retries.
 *
 * With no resolved source the `<img>` is an inline non-replaced box, `height` stops applying to
 * it, and the declared 194px collapses to a 16px line of alt text — which is the "image cut off"
 * in the ticket. There is no prop to opt out of that loader, so the promotion is done here.
 *
 * `data-src` is deliberately left in place: if mlcomponents' observer does fire afterwards it
 * writes the same URL back rather than the empty string it would derive from a missing
 * attribute. `loading="lazy"` is already on the element, so the browser still defers the fetch.
 *
 * Delete this once mlcomponents either fixes `DefaultImg` or accepts a `renderImage` prop.
 */
function promoteLazyImages(root: HTMLElement | null): void {
    root?.querySelectorAll<HTMLImageElement>('img[data-src]').forEach((img) => {
        const url = img.dataset.src;
        if (url && !img.getAttribute('src')) {
            img.setAttribute('src', url);
        }
    });
}

type S20ChoicesBuilderProps = Omit<S20ChoicesProps, 'backgroundGradient' | 'cards'> & {
    desktopPaddingTop?: PaddingSize;
    desktopPaddingBottom?: PaddingSize;
    mobilePaddingTop?: PaddingSize;
    mobilePaddingBottom?: PaddingSize;
    backgroundGradient?: MilanGradientColors | 'None' | 'default';
    cards: S20ChoiceCardProps[];
};

export function S20ChoicesSection(props: S20ChoicesBuilderProps) {
    const {
        desktopPaddingTop = 'large',
        desktopPaddingBottom = 'large',
        mobilePaddingTop = 'small',
        mobilePaddingBottom = 'small',
        backgroundGradient,
        cards,
        ...restProps
    } = props;

    const topPaddingClasses = `mcl-pt-${paddingMap[mobilePaddingTop]} lg:mcl-pt-${paddingMap[desktopPaddingTop]}`;
    const bottomPaddingClasses = `mcl-pb-${paddingMap[mobilePaddingBottom]} lg:mcl-pb-${paddingMap[desktopPaddingBottom]}`;

    const processedCards: S20ChoiceCardProps[] =
        cards?.map((card) => ({
            headline: card.headline,
            text: card.text,
            icon: card.icon,
            image: card.image,
            imageAltText: card.imageAltText,
            button: {
                label: card.button?.label || '',
                variant: card.button?.variant || 'primary',
                url: card.button?.url || '',
            },
        })) || [];

    const modifiedProps = {
        ...restProps,
        backgroundGradient: backgroundGradient === 'None' ? undefined : backgroundGradient,
        backgroundColor: props.backgroundColor || 'default',
        className: `mcl-mx-auto mcl-max-w-7xl ${topPaddingClasses} ${bottomPaddingClasses} [&_p]:mb-0`,
        cards: processedCards,
    };

    // Generate a new key when cards change to force re-render
    const newKeyOnCardsChange = useMemo(() => useImageKey(processedCards), [processedCards]);

    const backgroundColor = processBackgroundAndGradientColorMap({
        backgroundColor: props.backgroundColor || 'default',
        gradientColor: props.backgroundGradient || 'default',
    });

    const sectionRef = useRef<HTMLDivElement>(null);

    // Re-runs when the card set changes, which is also what remounts the section below.
    useEffect(() => {
        promoteLazyImages(sectionRef.current);
    }, [newKeyOnCardsChange]);

    return (
        <div className={`${backgroundColor}`} ref={sectionRef}>
            <S20Choices key={newKeyOnCardsChange} {...modifiedProps} />
        </div>
    );
}
